import React, { useState, useEffect, useContext } from 'react';
import { ExternalLink } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';

const BannerSidebar = () => {
  const { API, user } = useContext(AuthContext);
  const [banners, setBanners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, [user]);

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const res = await axios.get(`${API}/ads/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(res.data.banners || []);
    } catch {
      // silent fail
    } finally {
      setLoaded(true);
    }
  };

  const trackClick = (bannerId) => {
    try {
      const token = localStorage.getItem('helply_token');
      axios.post(`${API}/ads/${bannerId}/click`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    } catch {}
  };

  // Static GKV fallback banners shown when no ads created yet
  const fallbackBanners = [
    {
      id: 'fallback-1',
      title: 'GKV Consulting',
      description: 'Professional consulting services in Switzerland',
      bg_color: '#1e3a5f',
      text_color: '#ffffff',
      link_url: 'https://www.gkv-consulting.com'
    },
    {
      id: 'fallback-2',
      title: 'Advertise Here',
      description: 'Reach thousands of Swiss professionals and clients. Contact us to place your ad.',
      bg_color: '#e5e7eb',
      text_color: '#374151',
      link_url: null
    }
  ];

  const displayBanners = loaded && banners.length > 0 ? banners : (loaded ? fallbackBanners : []);

  if (!loaded) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-1">Sponsored</p>

      {displayBanners.map((banner) => (
        banner.link_url ? (
          <a
            key={banner.id}
            href={banner.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            style={{ backgroundColor: banner.bg_color || '#2563eb' }}
            onClick={() => trackClick(banner.id)}
            data-testid={`banner-${banner.id}`}
          >
            {banner.image_url && (
              <img
                src={banner.image_url}
                alt={banner.title}
                className="w-full h-20 object-cover"
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="px-3 py-2.5" style={{ color: banner.text_color || '#ffffff' }}>
              <p className="font-semibold text-xs leading-tight">{banner.title}</p>
              {banner.description && (
                <p className="text-xs mt-0.5 opacity-80 line-clamp-2">{banner.description}</p>
              )}
              <span className="inline-flex items-center gap-1 text-xs mt-1.5 opacity-70 hover:opacity-100 transition-opacity">
                <ExternalLink className="w-2.5 h-2.5" />
                Learn more
              </span>
            </div>
          </a>
        ) : (
          <div
            key={banner.id}
            className="rounded-lg overflow-hidden border border-gray-100 shadow-sm"
            style={{ backgroundColor: banner.bg_color || '#2563eb' }}
            data-testid={`banner-${banner.id}`}
          >
            {banner.image_url && (
              <img
                src={banner.image_url}
                alt={banner.title}
                className="w-full h-20 object-cover"
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="px-3 py-2.5" style={{ color: banner.text_color || '#ffffff' }}>
              <p className="font-semibold text-xs leading-tight">{banner.title}</p>
              {banner.description && (
                <p className="text-xs mt-0.5 opacity-80 line-clamp-2">{banner.description}</p>
              )}
            </div>
          </div>
        )
      ))}

      {/* Empty placeholder slots to fill the column */}
      {displayBanners.length < 6 && Array.from({ length: 6 - displayBanners.length }).map((_, i) => (
        <div
          key={`empty-${i}`}
          className="rounded-lg border-2 border-dashed border-gray-200 px-3 py-4 text-center"
          data-testid={`banner-empty-${i}`}
        >
          <p className="text-xs text-gray-300 font-medium">Ad Space</p>
        </div>
      ))}
    </div>
  );
};

export default BannerSidebar;
